import { CandidatesConst } from '@axiom/const';
import React from 'react';

import { Gutter } from '../../layout/Gutter/Gutter';
import { Badge } from '../../element/Badge/Badge';
import type { backgroundColorNames } from '../../../theme/axiom-theme';

export type BadgeKey = keyof typeof CandidatesConst.Badges;
export type BadgeValue = (typeof CandidatesConst.Badges)[BadgeKey];
type BackgroundColor =
  (typeof backgroundColorNames)[keyof typeof backgroundColorNames];

export type BadgeItem = BadgeKey | { key: BadgeKey; customText: string };

export type CandidateBadgesProps = {
  badges?: BadgeItem[];
};

const BADGE_COLORS: Record<BadgeValue, BackgroundColor> = {
  [CandidatesConst.Badges.AvailableNow]: 'forest green',
  [CandidatesConst.Badges.AvailableSoon]: 'light blue',
  [CandidatesConst.Badges.NotAvailable]: 'gray',
  [CandidatesConst.Badges.Contractor]: 'matcha',
};

const getBadgeKey = (badge: BadgeItem): BadgeKey =>
  typeof badge === 'string' ? badge : badge.key;

const getBadgeText = (badge: BadgeItem) => {
  const badgeKey = getBadgeKey(badge);
  const defaultText = CandidatesConst.Badges[badgeKey];

  return typeof badge === 'object' && badge.customText
    ? badge.customText
    : defaultText;
};

export const CandidateBadges = ({ badges }: CandidateBadgesProps) => {
  const distinctBadges = [
    ...new Map((badges ?? []).map(b => [getBadgeKey(b), b])).values(),
  ];

  if (distinctBadges.length === 0) {
    return null;
  }

  return (
    <div data-test="BADGES_CONTAINER">
      {distinctBadges.map(badge => {
        const badgeKey = getBadgeKey(badge);
        const badgeText = getBadgeText(badge);
        const badgeValue = CandidatesConst.Badges[badgeKey];

        return (
          <Gutter right="8px" key={badgeKey} as="span">
            <Badge name={badgeKey} background={BADGE_COLORS[badgeValue]}>
              {badgeText}
            </Badge>
          </Gutter>
        );
      })}
    </div>
  );
};
